import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormACAPenalties = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="ACA Forms Penalty for the Tax Year 2025 | TaxZerone"
          description="ACA Forms Penalties for 2025 explained—late filing, incorrect filing, ESR penalties, updated amounts, and how to avoid IRS penalty notices."
          keywords=""
          canonical="resources/information-returns/aca-forms-penalties/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          ACA Forms{' '}
          <span className="highlight-container">
            <span className="highlight">Penalties </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Avoid Costly IRS Penalties — File Your ACA Forms Accurately & On Time
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acapenalties`}
          >
            E-file now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/aca-forms-penalties/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    ACA Forms Penalties at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {'Employers and health coverage providers must '}
                        <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                          {'file ACA '}
                        </Link>
                        <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                          Forms
                        </Link>
                        {' to'}
                        {
                          ' report health coverage information to the IRS and individuals.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Filing Deadlines
                        </strong>
                        {': Furnish recipient copies by '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' and electronically file ACA forms with the IRS by '}
                        <strong style={{ fontWeight: 600 }}>March 31.</strong>
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Avoid IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $340 per return or statement
                        </strong>
                        {'; intentional disregard may result in penalties of '}
                        <strong style={{ fontWeight: 600 }}>
                          $680 per return
                        </strong>
                        {' with no maximum limit.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        {
                          'Failure to offer affordable, minimum value health coverage may also trigger '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          Employer Shared Responsibility (ESR) penalties under
                          IRC  §4980H
                        </strong>
                        {', including up to '}
                        <strong style={{ fontWeight: 600 }}>
                          $2,900 per full-time employee
                        </strong>
                        {' and '}
                        <strong style={{ fontWeight: 600 }}>
                          $4,350 per affected employee
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        {'Employers filing '}
                        <strong style={{ fontWeight: 600 }}>
                          10 or more ACA information returns
                        </strong>
                        {
                          ' must file electronically. Failure to file electronically when required may result in additional penalties.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        {
                          'Eligible employers may request penalty relief through '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          Reasonable Cause
                        </strong>
                        {' or '}
                        <strong style={{ fontWeight: 600 }}>
                          First-Time Abatement (FTA)
                        </strong>
                        {' if they meet IRS requirements.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '4px solid #0077b6',
                    px: 1.2,
                    py: 1.5,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Critical Deadline Alert for 2026
                  </Typography>
                  <Typography variant="body1">
                    <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                      {' '}
                      ACA forms
                    </Link>{' '}
                    for the 2025 tax year are due by March 2, 2026 (Furnish
                    copies to employees) and March 31, 2026 (electronic filing
                    to IRS). Penalties for non-compliance have increased
                    significantly for 2026. Missing these deadlines can result
                    in substantial financial penalties.
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The Affordable Care Act (ACA) requires Applicable Large
                  Employers (ALEs) to report health insurance coverage
                  information to the IRS and employees annually. Failure to file
                  these forms correctly and on time results in significant
                  penalties under IRC Sections 6721 and 6722. For the 2026 tax
                  year, penalties have been explained in detail below.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These penalties are separate from the Employer Shared
                  Responsibility (ESR) penalties under IRC Section 4980H, which
                  apply when employers fail to offer adequate coverage.
                  Understanding both types of penalties is essential for
                  comprehensive ACA compliance.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#over" sx={{ color: '#0202ff' }}>
                          ACA Forms Overview
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#types" sx={{ color: '#0202ff' }}>
                          Types of Penalties for 2026
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          Important Filing Deadlines
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Filing Mistakes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Avoid Penalties?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#respon" sx={{ color: '#0202ff' }}>
                          Responding to Penalty Notices
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#reason" sx={{ color: '#0202ff' }}>
                          Reasonable Cause Scenarios
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalty Calculations
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="over"
                >
                  ACA Forms Overview
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Four primary forms are involved in ACA reporting, each serving
                  a specific purpose in demonstrating compliance:
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }} sx={{}}>
                    <Link
                      href="/resources/information-returns/form-1095-c-instructions/"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: 4,
                          p: 2.5,
                          py: 3,
                          minHeight: '100%',
                          textAlign: 'center',
                          justifyContent: 'center',
                          alignContent: 'center',
                          backgroundColor: '#f0f2f9',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            boxShadow:
                              ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                          },
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                            mb: 1,
                            fontWeight: 600,
                          }}
                        >
                          1095-C
                        </Typography>
                        <Typography variant="body1">
                          1095-C Employer-Provided Health Insurance Offer and
                          Coverage Information Returns
                        </Typography>
                      </Paper>
                    </Link>
                  </Grid>

                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'center',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        1094-C
                      </Typography>
                      <Typography variant="body1">
                        Transmittal of Employer-Provided Health Insurance Offer
                        and Coverage Information Returns
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Link
                      href="/resources/information-returns/form-1095-b-instructions/"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: 4,
                          p: 2.5,
                          py: 3,
                          minHeight: '100%',
                          textAlign: 'center',
                          justifyContent: 'center',
                          alignContent: 'center',
                          backgroundColor: '#f0f2f9',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            boxShadow:
                              ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                          },
                        }}
                      >
                        <Typography
                          variant="h3"
                          sx={{
                            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                            mb: 1,
                            fontWeight: 600,
                          }}
                        >
                          1095-B
                        </Typography>
                        <Typography variant="body1">
                          Health Coverage (For Self-Insured Plans)
                        </Typography>
                      </Paper>
                    </Link>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'center',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        1094-B
                      </Typography>
                      <Typography variant="body1">
                        Transmittal of Health Coverage Information Returns
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Who Must File?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/resources/information-returns/form-1095-c-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Forms 1094-C and 1095-C
                      </Link>
                      :
                    </span>{' '}
                    Required for ALEs (50+ full-time equivalent employees in
                    prior year) to report coverage offers made to full-time
                    employees.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/resources/information-returns/form-1095-b-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Forms 1094-B and 1095-B
                      </Link>
                      :
                    </span>{' '}
                    Required for self-insured employers of any size to report
                    actual coverage provided.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="types"
                >
                  Types of ACA Penalties for 2026
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 19, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  1. Information Reporting Penalties (IRC  §6721 &  §6722)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These penalties apply to failures in filing information
                  returns and furnishing statements to employees. The amounts
                  are adjusted annually for inflation.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            colSpan={5}
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 17,
                            }}
                          >
                            Large Businesses with Gross Receipts of More Than $5
                            Million
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Time of correct filing
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Not more than 30 days late
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            31 days late - August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            After August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Intentional disregard
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2026 through 12-31-2026
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$683,000</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $130</span>/
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$2,049,000</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $340</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$4,098,500 </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $680</span> per
                            return or statement No limitation
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2025 through 12-31-2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$664,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $130</span>
                            per return or statement{' '}
                            <span style={{ fontWeight: 600 }}>
                              $1,993,500
                            </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $330</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$3,987,000 </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $660</span> per
                            return or statement No maximum
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2024 through 12-31-2024
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$630,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $120</span>/ per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$1,891,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $310</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$3,783,000 </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $630</span> per
                            return or statement No maximum
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Due 01-01-2023 through 12-31-202
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $50</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$588,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>$110</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$1,766,000</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>$290</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$3,532,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $580</span> per
                            return or statement No maximum
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            colSpan={5}
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 17,
                            }}
                          >
                            Small Businesses with Gross Receipts $5 Million or
                            Less
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Time of correct filing
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Not more than 30 days late
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            31 days late - August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            After August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Intentional disregard
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2026 through 12-31-2026
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$239,000 </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $130</span> per
                            return or statement Maximum -{' '}
                            <span style={{ fontWeight: 600 }}>
                              $683,000
                            </span>{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $340</span> per
                            return or statement Maximum -{' '}
                            <span style={{ fontWeight: 600 }}>
                              $1,366,000
                            </span>{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $680</span> per
                            return No maximum
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2025 through 12-31-2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement Maximum -
                            <span style={{ fontWeight: 600 }}>
                              $232,500
                            </span>{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $130</span> per
                            return or statement Maximum -{' '}
                            <span style={{ fontWeight: 600 }}>
                              $664,500
                            </span>{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $330</span> per
                            per return or statement Maximum -{' '}
                            <span style={{ fontWeight: 600 }}>
                              $1,329,000{' '}
                            </span>{' '}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $660</span> per
                            return No maximum
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Due 01-01-2024 through 12-31-2024
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $60</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$220,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $120</span>per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$630,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $310</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$1,261,000 </span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $630</span> per
                            return or statement No maximum
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Due 01-01-2023 through 12-31-202
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $50</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$206,000</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>$110</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$588,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>$290</span> per
                            return or statement{' '}
                            <span style={{ fontWeight: 600 }}>$1,177,500</span>{' '}
                            maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $580</span> per
                            return or statement No maximum
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 19, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  2. Employer Shared Responsibility Penalties (IRC  §4980H)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  These penalties apply when ALEs fail to offer affordable,
                  minimum value coverage, and at least one full-time employee
                  receives a premium tax credit through the Health Insurance
                  Marketplace.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }} sx={{}}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'center',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow:
                            ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        1095-C4980H(a) - No Offer Penalty
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 24, md: 20, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 700,
                          color: '#223d99',
                        }}
                      >
                        $2,900
                      </Typography>
                      <Typography variant="body1">
                        Per full-time employee (minus first 30) annually if
                        coverage not offered to 95% of FTEs
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }} sx={{}}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'center',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow:
                            ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        4980H(b) - Inadequate Coverage
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 24, md: 20, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 700,
                          color: '#223d99',
                        }}
                      >
                        $4,350
                      </Typography>
                      <Typography variant="body1">
                        Per full-time employee receiving premium tax credit if
                        coverage is unaffordable or lacks minimum value
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '4px solid #0077b6',
                    px: 1.2,
                    py: 1.5,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Combined Penalty Risk
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1.8 }}>
                    Employers can face both information reporting penalties AND
                    employer shared responsibility penalties simultaneously.
                  </Typography>
                  <Typography variant="body1">
                    <span style={{ fontWeight: 600 }}> For example,</span>{' '}
                    failing to{' '}
                    <Link
                      href="/aca-forms/e-file-form-1095c/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file 1095-C forms
                    </Link>{' '}
                    while also not offering adequate coverage could result in
                    compounding penalties totaling tens or hundreds of thousands
                    of dollars
                  </Typography>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="file"
                >
                  Important Filing Deadlines for 2026
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Learn about the aca forms deadline 2025 Tax Year
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                            }}
                          >
                            Action Required
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 16,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Furnish to 1095-C Employees
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Furnish to 1095-B covered Individuals
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Paper Filing with IRS
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            E-Filing with IRS
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 31, 2026
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b>{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Electronic Filing Mandatory
                  </span>
                  <br />
                  If you are required to file 10 or more information returns of
                  any single type (e.g., 10 or more{' '}
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1095 C form
                  </Link>
                  ), you MUST file electronically. This threshold applies
                  separately to each form type. Electronic filing provides
                  confirmation of receipt and reduces processing errors.
                </Alert>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }} sx={{}}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'left',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow:
                            ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                        },
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        Don’t Miss Your ACA Deadlines
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Need more time to{' '}
                        <Link href="/aca-forms/" sx={{ color: '#0202ff' }}>
                          file your ACA forms
                        </Link>
                        ? Submit your extension request correctly and avoid
                        costly IRS penalties.
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 250,
                          textAlign: 'left',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#223e98',
                            color: '#ffffff',
                            '&:hover, &:focus, &:active': {
                              color: '#fff',
                              background: '#172b6b',
                            },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acapenalties`}
                        >
                          File Form 8809 Now
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 4,
                        p: 2.5,
                        py: 3,
                        minHeight: '100%',
                        textAlign: 'left',
                        justifyContent: 'center',
                        alignContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                          mb: 1,
                          fontWeight: 600,
                        }}
                      >
                        Need more time to furnish employee copies?
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        If you require a 30-day extension to provide statements
                        to recipients, you can request additional time by{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          filing Form 15397
                        </Link>
                        .
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'left',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#223e98',
                            color: '#ffffff',
                            '&:hover, &:focus, &:active': {
                              color: '#fff',
                              background: '#172b6b',
                            },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acapenalties`}
                        >
                          File Now
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="common"
                >
                  Common Filing Mistakes
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Understanding the most frequent errors can help you avoid
                  costly penalties:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Incorrect or Missing Taxpayer Identification Numbers
                      (TINs):
                    </span>{' '}
                    Employee Social Security Numbers must exactly match IRS
                    records. Even minor discrepancies cause penalties.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Wrong Code Entries:
                    </span>{' '}
                    Using incorrect codes in Part II of{' '}
                    <Link
                      href="/resources/information-returns/form-1095-c-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 1095-C
                    </Link>{' '}
                    (Lines 14-16) to indicate offers of coverage, safe harbors,
                    or coverage status.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Inaccurate Employee Classification:
                    </span>{' '}
                    Incorrectly classifying employees as part-time when they
                    should be considered full-time under ACA rules (averaging
                    30+ hours/week).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Missing or Late Employee Statements:
                    </span>{' '}
                    Failing to provide 1095-C form or 1095-B to employees by
                    March 2.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Incomplete Coverage Information:
                    </span>{' '}
                    Not reporting all 12 months of coverage information or
                    leaving required fields blank.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Wrong Filing Method:
                    </span>{' '}
                    Filing on paper when electronic filing is required (10+
                    returns).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Failure to Aggregate Controlled Groups:
                    </span>{' '}
                    Not combining employees across commonly controlled entities
                    when determining ALE status.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Incorrect Affordability Calculations:
                    </span>{' '}
                    Miscalculating whether coverage meets the affordability
                    threshold (9.02% of household income for 2025).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Not Filing Corrected Returns:
                    </span>{' '}
                    Discovering errors but failing to file corrected returns
                    promptly to minimize penalties.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Missing Forms Entirely:
                    </span>{' '}
                    Complete failure to file, often due to not recognizing ALE
                    status or self-insured plan reporting requirements.{' '}
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '4px solid #0077b6',
                    px: 1.2,
                    py: 1.5,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ErrorOutlineIcon sx={{ mr: 1, color: '#0077b6' }} />{' '}
                    Important:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    The SSN Verification Problem
                  </Typography>
                  <Typography variant="body1">
                    One of the most common penalty cause is SSN mismatches. Even
                    if you collected the SSN from the employee correctly,
                    variations in legal names (e.g., "Robert" vs "Bob,"
                    hyphenated names; suffixes like Jr. or Sr.) can cause
                    mismatches with IRS records. Always verify employee
                    information against W-2 data and consider using the IRS TIN
                    Matching service before filing
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to Avoid ACA Forms Penalties?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Implementing these best practices will significantly reduce
                  your penalty risk:
                </Typography>
                <Box component="ol" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Maintain Comprehensive Records Year-Round
                    </span>{' '}
                    <br />
                    Track all employee hours, coverage offers, and enrollment
                    elections throughout the year—not just at filing time. Use
                    the measurement, administrative, and stability period method
                    consistently. Document all safe harbor calculations and
                    maintain evidence of affordability.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Verify Employee Information Early
                    </span>{' '}
                    <br />
                    Collect and verify SSNs, legal names, and addresses well
                    before the filing deadline. Double-check with{' '}
                    <Link
                      href="/resources/information-returns/form-w-2-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      W2
                    </Link>{' '}
                    information and payroll records. Consider using the{' '}
                    <Link
                      href="/request-form-w9-online-free/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS TIN Matching
                    </Link>{' '}
                    Program to identify discrepancies before filing.
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Invest in Reliable ACA Software
                    </span>{' '}
                    <br />
                    Use specialized ACA reporting software or partner with an
                    experienced provider. Quality software will validate codes,
                    calculate affordability, track employees across measurement
                    periods, and identify potential errors before submission.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Conduct Internal Audits
                    </span>{' '}
                    <br />
                    Before filing, audit a sample of forms for accuracy. Check
                    the aca codes to match actual coverage offers, employee
                    classifications are correct, and all 12 months of data are
                    reported. Review aggregation rules for controlled groups.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      File Electronically and Early
                    </span>{' '}
                    <br />
                    Don&apos;t wait until the deadline. Electronic filing
                    through the IRS FIRE system provides immediate confirmation
                    and allows time to correct rejected filings. Aim to file by
                    before the March 31 deadline.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Stay Informed on IRS Guidance
                    </span>{' '}
                    <br />
                    Monitor IRS announcements, FAQs, and instructions for
                    changes. Penalty amounts adjust annually for inflation, and
                    the IRS periodically updates reporting requirements and code
                    definitions.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Consider Professional Assistance
                    </span>{' '}
                    <br />
                    For complex situations—multiple entities, aggregated groups,
                    international employees, or self-insured plans—consider
                    working with ACA compliance specialists, benefits
                    consultants, or tax professionals with specific ACA
                    reporting expertise.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="respon"
                >
                  What to Do If You Receive a Penalty Notice?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Common notices include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>IRS Letter 226J (ESR penalties) </li>
                  <li>IRS Notice 972CG (information return penalties) </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Steps:
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Review notice carefully </li>
                  <li>Compare IRS data with your records </li>
                  <li>File corrections immediately if needed </li>
                  <li>Respond within 30 days </li>
                  <li>Request abatement if applicable </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: '#f1f1f1',
                    border: '2px solid #ccc',
                    borderRadius: 4,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ðŸ’¡First-Time Penalty Abatement (FTA)
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    If you have a clean compliance history (no penalties in the
                    prior three years) and have filed all required returns, you
                    may qualify for administrative penalty relief under FTA.
                    This can completely eliminate penalties without needing to
                    prove reasonable cause. Request FTA when responding to the
                    penalty notice.{' '}
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="reason"
                >
                  Reasonable Cause Scenarios
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Unavoidable Absence:
                    </span>{' '}
                    Serious illness, death, or incapacitation of the person
                    responsible for filing{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Fire, Casualty, or Natural Disaster:
                    </span>{' '}
                    Events that destroyed records or made filing impossible{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> System Failure:</span>{' '}
                    Technology failures beyond your control (must demonstrate
                    the failure and your attempts to mitigate)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Undue Economic Hardship:
                    </span>{' '}
                    Filing would cause significant financial hardship (rarely
                    accepted)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> First-Time Filer:</span>{' '}
                    Made a good faith effort to comply but made errors (must
                    show the effort and steps taken)
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Unavailability of Records:
                    </span>{' '}
                    Records were unavailable despite reasonable efforts to
                    obtain them.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  What Doesn&apos;t Qualify
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The following are NOT considered reasonable cause: lack of
                  knowledge of the filing requirements, reliance on someone else
                  to file without monitoring their performance, staff turnover,
                  being too busy, software problems if you didn't verify the
                  output, or financial difficulties. The burden of proof is
                  entirely on you to demonstrate reasonable cause.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="pen"
                >
                  How Penalties Are Calculated?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Understanding the calculation methodology helps you estimate
                  potential exposure:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            colSpan={2}
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 16,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Filing TypeInformation Reporting Penalties ( §6721 &
                            §6722)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Calculation Elements
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Explanation
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Count the Violations
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Each form is not filed, filed late, or filed
                            incorrectly = 1 violation. <br />
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Example:
                            </span>{' '}
                            100 missing Forms 1095-C = 100 violations.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Determine the Timeline
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Identify when the error was corrected:
                            <ul>
                              <li style={{ marginBottom: 8 }}>
                                Within 30 days{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>By August 1</li>
                              <li style={{ marginBottom: 8 }}>
                                {' '}
                                After August 1{' '}
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                Intentional disregard
                              </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Apply Per-Return Penalty (2026)
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Multiply violations by applicable rate:
                            <ul>
                              <li style={{ marginBottom: 8 }}>
                                $60 (≤30 days)
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                $130 (by August 1)
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                {' '}
                                $340 (after August 1)
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                $680 (intentional disregard)
                              </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Apply Annual Maximum
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 15,
                            }}
                          >
                            Total penalty cannot exceed annual cap for that tier
                            (except intentional disregard – no maximum).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                            }}
                          >
                            Separate Calculations Required
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              backgroundColor: '#fff',
                              fontSize: 15,
                            }}
                          >
                            Calculate separately for:
                            <ul>
                              <li style={{ marginBottom: 8 }}>
                                Failure to file with IRS ( §6721)
                              </li>
                              <li style={{ marginBottom: 8 }}>
                                Failure to furnish employees ( §6722).
                              </li>
                            </ul>
                            Both may apply to the same forms.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            colSpan={4}
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 16,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Employer Shared Responsibility Penalties ( §4980H)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Penalty Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            When It Applies
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            2026 Annual Amount
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            How It’s Calculated
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> 4980H(a)</span> –
                            No Offer Penalty
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            ALE fails to offer coverage to at least 95% of
                            full-time employees
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            $2,900 per full-time employee
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            (Total Full-Time Employees – 30) × $2,900 (prorated
                            monthly)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> 4980H(b)</span> –
                            Inadequate Coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                            }}
                          >
                            Coverage offered but unaffordable or lacks minimum
                            value AND employee receives Premium Tax Credit
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                            }}
                          >
                            $4,350 per affected employee
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              fontSize: 15,
                              backgroundColor: '#fff',
                            }}
                          >
                            Number of employees receiving Premium Tax Credit ×
                            $4,350 (prorated monthly)
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on:
                        <ul>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/affordable-care-act/employers"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              IRS Affordable Care Act Tax Provisions for
                              Employers
                            </Link>{' '}
                          </li>

                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              IRS instructions of Form 1095-C
                            </Link>{' '}
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              IRS instructions of Form 1095-B
                            </Link>{' '}
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/affordable-care-act/employers/questions-and-answers-on-employer-shared-responsibility-provisions-under-the-affordable-care-act"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              IRS Q&A on Employer Shared Responsibility
                              Provisions
                            </Link>
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/affordable-care-act/employers/information-reporting-by-applicable-large-employers"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Information Reporting by Applicable Large
                              Employers
                            </Link>
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/affordable-care-act"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              ACA Tax Provisions{' '}
                            </Link>{' '}
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.dol.gov/agencies/ebsa/laws-and-regulations/laws/affordable-care-act"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Department of Labor ACA resources
                            </Link>{' '}
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/p1586.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Publication 1586{' '}
                            </Link>{' '}
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Stay ACA-Compliant & Avoid IRS Penalties
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 4500,
                color: '#333333',
                mb: 2,
              }}
            >
              Whether you’re filing Forms 1094/1095 or managing Employer Shared
              Responsibility compliance under{' '}
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              Section 4980H, accuracy and timely filing are important.
            </Typography>
            {/* <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              The filing cost of each form can go down up to <b>$0.59</b>
            </Typography> */}

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=acapenalties`}
              >
                E-file now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormACAPenalties;
